import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import "./DualEditorForm.scss";
import debounce from "lodash/debounce";
import {
    flattenJson,
    unflattenJson,
    getLabelFromKey,
    detectInputType,
    isReadOnlyPath,
    validateJsonAndApply,
    getJsonErrorFromException,
} from "./DualEditorForm.utils";
import {
    CONSOLE_READ_ONLY_PATHS,
    CONSOLE_ANGLE_FIELDS,
    CONSOLE_EMPTY_ARRAY_MARKER,
    CONSOLE_REGEX,
    CONSOLE_ANGLE_RANGE,
    CONSOLE_ERROR_MESSAGES,
    CONSOLE_DEFAULT_EXPANDED_PATHS,
    VALID_JSON_EVENT,
    JSON_VALIDATE_DEBOUNCE_MS,
    JSON_INDENT_SPACES,
    EMPTY_JSON_STR,
} from "../../../../../../utils/constants";
import CommandDataHeader from "./CommandDataHeader";
import JsonEditorView from "./JsonEditorView";
import FormFieldsView from "./FormFieldsView";
import { CaretDownSvg, CaretRightSvg } from "../../../../../CommonComponents/svgs/Svgs";

export interface DualEditorFormProps {
    jsonData: object;
    onJsonChange?: (data: object) => void;
    setPreviewTeleCommandData: (data: object) => void;
    setBusFormData: (data: object) => void;
    selectedTelecommand: unknown;
    scrollClassName?: string;
    mode?: string;
    isCustomTc?: boolean;
}

const stringifyJson = (obj: object) => JSON.stringify(obj, null, JSON_INDENT_SPACES);

const getDefaultExpandedPaths = (isCustomTc = false): Record<string, boolean> => {
    const defaultExpanded: Record<string, boolean> = {};
    for (let i = 0; i < CONSOLE_READ_ONLY_PATHS.length; i++) {
        defaultExpanded[CONSOLE_READ_ONLY_PATHS[i]] = true;
    }
    defaultExpanded[CONSOLE_DEFAULT_EXPANDED_PATHS.SEQUENCE_DATA] = true;
    defaultExpanded[CONSOLE_DEFAULT_EXPANDED_PATHS.COMMAND_DATA] = true;
    defaultExpanded[CONSOLE_DEFAULT_EXPANDED_PATHS.COMMAND_HEADER] = isCustomTc ? true : false;
    return defaultExpanded;
};

const DualEditorForm = ({
    jsonData,
    onJsonChange,
    setPreviewTeleCommandData,
    setBusFormData,
    selectedTelecommand,
    scrollClassName = "",
    mode = "",
    isCustomTc = false,
}: DualEditorFormProps) => {
    const isCustomTcMode = Boolean(isCustomTc || mode === "customTc");
    const [formData, setFormData] = useState<Record<string, unknown>>(() => flattenJson(jsonData) as Record<string, unknown>);
    const formDataRef = useRef<Record<string, unknown>>(formData);
    useEffect(() => {
        formDataRef.current = formData;
    }, [formData]);
    const [isJsonView, setIsJsonView] = useState(false);
    const [jsonInput, setJsonInput] = useState<string | object>(() => stringifyJson(jsonData));
    const [jsonError, setJsonError] = useState<string | null>(null);
    const [originalKeys, setOriginalKeys] = useState<Set<string>>(() => new Set(Object.keys(flattenJson(jsonData) as Record<string, unknown>)));
    const [isCommandDataJson, setIsCommandDataJson] = useState(false);
    const [originalData, setOriginalData] = useState<Record<string, unknown>>({});
    const [expandedPaths, setExpandedPaths] = useState<Record<string, boolean>>(() => getDefaultExpandedPaths(isCustomTcMode));
    const readOnly = false;
    const editorRef = useRef<any>(null);
    const monacoRef = useRef<any>(null);
    const jsonEditorKeyRef = useRef(0);

    const sanitizeNumericBlanks = useCallback(
        (flatData: Record<string, unknown>) => {
            const out = { ...flatData };
            for (const key of Object.keys(out)) {
                if (out[key] !== "" || isReadOnlyPath(key, isCustomTcMode)) continue;
                const baseName = (key as string).replace(CONSOLE_REGEX.ARRAY_INDEX, "");
                    if (
                        (CONSOLE_ANGLE_FIELDS as readonly string[]).includes(baseName) ||
                        typeof originalData[key] === "number"
                    ) {
                        out[key] = 0;
                    }
            }
            return out;
        },
        [originalData, isCustomTcMode]
    );

    // Reset form state when the selected telecommand changes (not on every jsonData update,
    // which would overwrite user edits mid-typing).
    useEffect(() => {
        const flatData = flattenJson(jsonData) as Record<string, unknown>;
        setOriginalData(flatData);
        setFormData(flatData);
        formDataRef.current = flatData;
        setOriginalKeys(new Set(Object.keys(flatData)));
        if (!isJsonView) {
            setJsonInput(stringifyJson(jsonData));
        }
        window.dispatchEvent(new CustomEvent(VALID_JSON_EVENT, { detail: { buttonOn: true } }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTelecommand]);

    useEffect(() => {
        setExpandedPaths(getDefaultExpandedPaths(isCustomTcMode));
    }, [selectedTelecommand, isCustomTcMode]);

    useEffect(() => {
        const emptyPaths = Object.keys(formData).filter((k) => formData[k] === CONSOLE_EMPTY_ARRAY_MARKER);
        if (emptyPaths.length === 0) return;
        const updated = { ...formData };
        for (const path of emptyPaths) {
            delete updated[path];
            updated[path + "[0]"] = 0;
        }
        setFormData(updated);
    }, [formData]);

    useEffect(() => {
        const hasBlanks = Object.keys(formData).some(
            (k) =>
                formData[k] === "" ||
                formData[k] === "-" ||
                formData[k] === CONSOLE_EMPTY_ARRAY_MARKER
        );
        if (hasBlanks) return;
        const restored = unflattenJson(sanitizeNumericBlanks(formData));
        onJsonChange?.(restored);
    }, [formData, onJsonChange, sanitizeNumericBlanks]);

    const getValidJsonInput = useMemo(() => {
        return typeof jsonInput === "string" ? jsonInput : stringifyJson(jsonInput as object);
    }, [jsonInput]);

    // Debounce validation to prevent cursor jumping on every keystroke
    const validateJsonDebounced = useMemo(
        () => debounce((input: string) => {
            if (!input || input.length === 0) return;

            const result = validateJsonAndApply(input, { originalData, originalKeys, isCustomTc: isCustomTcMode });

            if (result.success && result.parsed) {
                setPreviewTeleCommandData(result.parsed);
                setBusFormData(result.parsed);
                setJsonError(null);
                window.dispatchEvent(new CustomEvent(VALID_JSON_EVENT, { detail: { buttonOn: true } }));
            } else {
                setJsonError(result.error ?? CONSOLE_ERROR_MESSAGES.INVALID_JSON);
                window.dispatchEvent(new CustomEvent(VALID_JSON_EVENT, { detail: { buttonOn: false } }));
            }
        }, JSON_VALIDATE_DEBOUNCE_MS),
        [originalData, originalKeys, setPreviewTeleCommandData, setBusFormData, isCustomTcMode]
    );

    useEffect(() => {
        if (!isJsonView) return;
        const validJsonInput = getValidJsonInput;
        if (!validJsonInput || Object.keys(validJsonInput).length === 0) return;
        validateJsonDebounced(validJsonInput);

        return () => validateJsonDebounced.cancel();
    }, [isJsonView, getValidJsonInput, validateJsonDebounced]);

    const toggleExpand = useCallback((path: string) => {
        setExpandedPaths((prev) => {
            const newExpanded = { ...prev };
            newExpanded[path] = !prev[path];
            return newExpanded;
        });
    }, []);

    const handleChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const { name, type, value, checked } = e.target;
            if (isReadOnlyPath(name, isCustomTcMode)) return;

            let newVal: string | boolean | undefined | number;
            if (type === "checkbox") {
                newVal = checked;
            } else {
                const baseName = name.replace(CONSOLE_REGEX.ARRAY_INDEX, "");
                // Use originalData as the source of truth for type detection.
                // "in" check (not ??) ensures we use originalData even when its value is 0, null, or "".
                const currentData = formDataRef.current;
                const typeSource = name in originalData ? originalData[name] : (currentData[name] ?? formData[name]);
                const inputType = detectInputType(name, typeSource);
                
                // Only validate numeric format for number fields
                if (inputType === "number") {
                    if ((value.match(/-/g) || []).length > 1) return;
                    if (!CONSOLE_REGEX.INTEGER_INPUT.test(value)) return;

                    if (value === "" && name.includes("[")) {
                        const arrayPath = name.substring(0, name.lastIndexOf("["));
                        const arrayKeys = Object.keys(currentData).filter(
                            (key) => key.startsWith(arrayPath) && key.includes("[")
                        );
                        if (arrayKeys.length === 1) {
                            const updatedFormData = { ...currentData };
                            delete updatedFormData[name];
                            updatedFormData[arrayPath] = [0];
                            formDataRef.current = updatedFormData;
                            setFormData(updatedFormData);
                            setBusFormData(unflattenJson(sanitizeNumericBlanks(updatedFormData)));
                            return;
                        }
                    }

                    if (value === "" || value === "-") {
                        newVal = value;
                    } else if (value) {
                        const sanitized = CONSOLE_REGEX.LEADING_ZEROS.test(value)
                            ? value.replace(/^0+/, "") || "0"
                            : value;
                        // Only set as number if it's a valid number
                        if (!isNaN(Number(sanitized))) {
                            newVal = Number(sanitized);
                        } else {
                            newVal = sanitized;
                        }
                        if ((CONSOLE_ANGLE_FIELDS as readonly string[]).includes(baseName)) {
                            const num = Number(newVal);
                            if (!isNaN(num) && (num < CONSOLE_ANGLE_RANGE.MIN || num > CONSOLE_ANGLE_RANGE.MAX))
                                return;
                        }
                    } else {
                        newVal = undefined;
                    }
                } else {
                    // For non-numeric fields (strings, emails, etc.), accept value as-is
                    newVal = value;
                }
            }

            const updatedFormData = { ...formDataRef.current, [name]: newVal };
            formDataRef.current = updatedFormData;
            setFormData(updatedFormData);
            if (newVal !== "" && newVal !== undefined && newVal !== "-") {
                setBusFormData(unflattenJson(sanitizeNumericBlanks(updatedFormData)));
            }
        },
        [formData, originalData, sanitizeNumericBlanks, setBusFormData, isCustomTcMode]
    );

    const renderCheckboxControl = useCallback(
        ({
            name,
            checked,
            disabled,
            ariaLabel,
        }: {
            name: string;
            checked: boolean;
            disabled: boolean;
            ariaLabel: string;
        }) => (
            <label className={`dynamic-checkbox-control ${disabled ? "disabled" : ""}`}>
                <input
                    type="checkbox"
                    name={name}
                    checked={checked}
                    onChange={handleChange}
                    disabled={disabled}
                    className="dynamic-checkbox-input"
                    aria-label={ariaLabel}
                />
                <span className="dynamic-checkbox-box" aria-hidden="true" />
            </label>
        ),
        [handleChange]
    );

    const handleToggleView = useCallback(() => {
        if (isJsonView) {
            if (editorRef.current) {
                editorRef.current.dispose?.();
                editorRef.current = null;
            }
            isUserEditingRef.current = false;
            setIsJsonView(false);
            try {
                if (!jsonInput || Object.keys(jsonInput).length === 0) return;
                const validJsonInput = getValidJsonInput;

                if (validJsonInput && Object.keys(validJsonInput).length > 0) {
                    const result = validateJsonAndApply(validJsonInput, {
                        originalData,
                        originalKeys,
                        isCustomTc: isCustomTcMode,
                    });

                    if (!result.success) {
                        throw new Error(result.error);
                    }

                    if (result.parsed) {
                        const flatParsed = flattenJson(result.parsed) as Record<string, unknown>;
                        formDataRef.current = flatParsed;
                        setFormData(flatParsed);
                        setPreviewTeleCommandData(result.parsed);
                        setBusFormData(result.parsed);
                        setJsonError(null);
                        window.dispatchEvent(new CustomEvent(VALID_JSON_EVENT, { detail: { buttonOn: true } }));
                        setIsJsonView(false);
                    }
                }
            } catch (e) {
                setJsonError(getJsonErrorFromException(e as Error));
                window.dispatchEvent(new CustomEvent(VALID_JSON_EVENT, { detail: { buttonOn: false } }));
            }
        } else {
            isUserEditingRef.current = false;
            // Coerce any string values back to their original numeric type before
            // building the JSON string, in case type was lost during editing.
            const coercedFormData = { ...formDataRef.current };
            for (const key of Object.keys(coercedFormData)) {
                if (typeof coercedFormData[key] === "string" && typeof originalData[key] === "number") {
                    const n = Number(coercedFormData[key]);
                    if (!isNaN(n)) coercedFormData[key] = n;
                }
            }
            setJsonInput(stringifyJson(unflattenJson(coercedFormData)));
            setIsJsonView(true);
        }
    }, [isJsonView, jsonInput, getValidJsonInput, originalData, originalKeys, formData, setPreviewTeleCommandData, setBusFormData, isCustomTcMode]);

    const handleBtnClick = useCallback(() => {
        handleToggleView();
        setIsCommandDataJson((prev) => !prev);
    }, [handleToggleView]);

    const renderNestedForm = useCallback(
        (data: Record<string, unknown>, parentKey = "", depth = 0): React.ReactNode => {
            const entries = Object.entries(data);
            const orderedEntries =
                depth === 0
                    ? [...entries].sort(([, a], [, b]) => {
                        const aIsNested = a !== null && typeof a === "object";
                        const bIsNested = b !== null && typeof b === "object";
                        return Number(aIsNested) - Number(bIsNested);
                    })
                    : entries;

            return orderedEntries.map(([key, value]) => {
                const currentPath = parentKey ? `${parentKey}.${key}` : key;
                const isExpanded = expandedPaths[currentPath] ?? false;
                const isReadOnly = isReadOnlyPath(currentPath, isCustomTcMode);
                const isObject = value && typeof value === "object" && !Array.isArray(value);
                const isArray = Array.isArray(value);
                const isDuplicateCommandMetadata =
                    (!isCustomTcMode && key === "commandId") ||
                    (depth === 0 && (key === "commandName" || key === "commandType"));

                if (isDuplicateCommandMetadata) return null;

                if (isObject || isArray) {
                    return (
                        <div key={currentPath} className="form-group" style={{ marginLeft: "10px" }}>
                            <button
                                type="button"
                                className="group-header"
                                onClick={() => toggleExpand(currentPath)}
                                aria-expanded={isExpanded}
                                aria-label={`${isExpanded ? "Collapse" : "Expand"} ${key}`}
                                style={{ display: "flex", alignItems: "center", justifyContent: "flex-start" }}
                            >
                                <span className={`collapse-toggle ${isExpanded ? "expanded" : "collapsed"}`}>
                                    {isExpanded ? <CaretDownSvg /> : <CaretRightSvg />}
                                </span>
                                <div className="group-label">{key}</div>
                            </button>
                            {isExpanded && (
                                <div className={isArray ? "array-container" : "group-children"}>
                                    {isArray ? (
                                        Array.isArray(value) && value.length > 0 ? (
                                             value.map((item, index) => {
                                                const arrayItemPath = `${currentPath}[${index}]`;
                                                const arrayItemType = detectInputType(arrayItemPath, item);
                                                const arrayItemReadOnly = isReadOnlyPath(arrayItemPath, isCustomTcMode);
                                                return typeof item === "object" ? (
                                                    <div key={arrayItemPath} className="array-item-group">
                                                        <div className="array-index">[{index}]</div>
                                                        <div className="array-item-content">
                                                            {renderNestedForm(
                                                                item as Record<string, unknown>,
                                                                arrayItemPath,
                                                                depth + 1
                                                            )}
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div
                                                        key={arrayItemPath}
                                                        className={`array-item ${arrayItemType === "checkbox" ? "checkbox-array-item" : ""}`}
                                                    >
                                                        <label>[{index}]</label>
                                                        {arrayItemType === "checkbox" ? (
                                                            renderCheckboxControl({
                                                                name: arrayItemPath,
                                                                checked: !!item,
                                                                disabled: arrayItemReadOnly,
                                                                ariaLabel: `Item ${index}`,
                                                            })
                                                        ) : (
                                                            <input
                                                                type={arrayItemType}
                                                                name={arrayItemPath}
                                                                value={String(item).replace(
                                                                    CONSOLE_REGEX.LEADING_ZEROS_REPLACE,
                                                                    ""
                                                                )}
                                                                onChange={handleChange}
                                                                disabled={arrayItemReadOnly}
                                                                className={`${arrayItemType === "number" ? "no-spinner" : ""} ${arrayItemReadOnly ? "read-only-field" : ""}`}
                                                            />
                                                        )}
                                                    </div>
                                                );
                                            })
                                        ) : (
                                            <div className="array-item">
                                                <label>[0]</label>
                                                <input
                                                    type="number"
                                                    name={`${currentPath}[0]`}
                                                    value={0}
                                                    onChange={handleChange}
                                                    disabled={isReadOnlyPath(`${currentPath}[0]`, isCustomTcMode)}
                                                    className="no-spinner"
                                                />
                                            </div>
                                        )
                                    ) : (
                                        renderNestedForm(value as Record<string, unknown>, currentPath, depth + 1)
                                    )}
                                </div>
                            )}
                        </div>
                    );
                } else {
                    const type = detectInputType(currentPath, value);
                    const labelText = getLabelFromKey(currentPath);
                    return (
                        <div
                            className={`form-field ${type === "checkbox" ? "checkbox-field" : ""}`}
                            key={currentPath}
                            style={{ marginLeft: "10px" }}
                        >
                            {type === "checkbox" ? (
                                <>
                                    <span className="checkbox-field-label">{labelText}</span>
                                    {renderCheckboxControl({
                                        name: currentPath,
                                        checked: !!value,
                                        disabled: isReadOnly,
                                        ariaLabel: labelText,
                                    })}
                                </>
                            ) : (
                                <>
                                    <label>{labelText}</label>
                                    <input
                                        type="text"
                                        inputMode={type === "number" ? "numeric" : "text"}
                                        name={currentPath}
                                        value={
                                            value !== null && value !== undefined && value !== ""
                                                ? String(value)
                                                : ""
                                        }
                                        onWheel={(e) => {
                                            if (type === "number") e.currentTarget.blur();
                                        }}
                                        onChange={handleChange}
                                        onBlur={(e) => {
                                            const val = e.target.value.trim();
                                            const currentData = formDataRef.current;

                                            // Only parse as number if field is numeric type
                                            if (type === "number") {
                                                if (val === "" || val === "-") {
                                                    const updatedFormData = { ...currentData, [e.target.name]: 0 };
                                                    formDataRef.current = updatedFormData;
                                                    setFormData(updatedFormData);
                                                    setBusFormData(unflattenJson(sanitizeNumericBlanks(updatedFormData)));
                                                } else {
                                                    const num = parseInt(val, 10);
                                                    if (!isNaN(num)) {
                                                        const updatedFormData = { ...currentData, [e.target.name]: num };
                                                        formDataRef.current = updatedFormData;
                                                        setFormData(updatedFormData);
                                                        setBusFormData(unflattenJson(sanitizeNumericBlanks(updatedFormData)));
                                                    }
                                                }
                                            } else {
                                                // For non-numeric types (strings, etc.), accept value as-is
                                                const updatedFormData = { ...currentData, [e.target.name]: val };
                                                formDataRef.current = updatedFormData;
                                                setFormData(updatedFormData);
                                                setBusFormData(unflattenJson(sanitizeNumericBlanks(updatedFormData)));
                                            }
                                        }}
                                        disabled={isReadOnly}
                                        className={`${type === "number" ? "no-spinner" : ""} ${isReadOnly ? "read-only-field" : ""}`}
                                    />
                                </>
                            )}
                        </div>
                    );
                }
            });
        },
        [expandedPaths, toggleExpand, handleChange, renderCheckboxControl, formData, sanitizeNumericBlanks, setBusFormData, isCustomTcMode]
    );

    const formContent = useMemo(
        () => renderNestedForm(unflattenJson(formData) as Record<string, unknown>),
        // eslint-disable-next-line react-hooks/exhaustive-deps -- renderNestedForm already depends on expandedPaths
        [formData, renderNestedForm]
    );
    const programmaticRef = useRef(false);
    const isUserEditingRef = useRef(false);
    const lastValidJsonRef = useRef<string>(stringifyJson(jsonData));

    const setEditorValueFromExternal = useCallback(
        (newJson: string) => {
            if (!editorRef.current) {
                setJsonInput(newJson);
                return;
            }
            const current = editorRef.current.getValue();
            if (current === newJson) {
                return;
            }
            // Don't override editor value while user is actively editing
            if (isUserEditingRef.current) {
                return;
            }
            programmaticRef.current = true;
            editorRef.current.setValue(newJson);
            setJsonInput(newJson);
            setTimeout(() => (programmaticRef.current = false), 0);
        },
        []
    );

    const onChangeJsonObject = useCallback((obj: string | object) => {
        setJsonInput(obj);
    }, []);

    const validateAndSend = useRef(
        debounce((input: string | object) => {
            if (!input) return;
            try {
                if (typeof input === "object" && input !== null) {
                    onChangeJsonObject(input);
                } else if (typeof input === "string") {
                    const parsed = JSON.parse(input);
                    onChangeJsonObject(parsed);
                }
            } catch (e) {
                console.error("JSON parsing error:", e);
            }
        }, JSON_VALIDATE_DEBOUNCE_MS)
    ).current;

    useEffect(() => {
        validateAndSend(jsonInput);
        return () => validateAndSend.cancel();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- run only on mount for initial validation
    }, []);

    useEffect(() => {
        if (!editorRef.current) return;
        if (!jsonInput || jsonInput === "") {
            const currentValue = editorRef.current.getValue();
            if (currentValue.trim() !== "") {
                editorRef.current.setValue("");
            }
        }
    }, [jsonInput]);

    const handleJsonChange = useCallback((value: string | undefined) => {
        if (programmaticRef.current) {
            programmaticRef.current = false;
            return;
        }

        // Check if user tried to edit a read-only field
        if (value && !isCustomTcMode) {
            try {
                const newParsed = JSON.parse(value);
                const newFlat = flattenJson(newParsed);

                // Check each read-only path
                for (let i = 0; i < CONSOLE_READ_ONLY_PATHS.length; i++) {
                    const path = CONSOLE_READ_ONLY_PATHS[i];
                    if (path in newFlat && path in originalData && newFlat[path] !== originalData[path]) {
                        // User tried to edit a read-only field - revert editor and show error
                        setJsonError(`Cannot modify read-only field: '${path}'`);

                        // Revert editor to last valid state
                        if (editorRef.current && lastValidJsonRef.current) {
                            programmaticRef.current = true;
                            editorRef.current.setValue(lastValidJsonRef.current);
                            setJsonInput(lastValidJsonRef.current);
                        }
                        return;
                    }
                }
                // Valid change - update last valid state and clear error
                lastValidJsonRef.current = value;
                setJsonError(null);
            } catch {
                // JSON parse error - will be caught by main validation
            }
        }

        isUserEditingRef.current = true;
        setJsonInput(value ?? "");
    }, [originalData, isCustomTcMode]);

    useEffect(() => {
        // Reset editing flag when command changes to allow editor update
        isUserEditingRef.current = false;
        const newJsonStr = selectedTelecommand ? stringifyJson(jsonData) : EMPTY_JSON_STR;
        lastValidJsonRef.current = newJsonStr;
        setEditorValueFromExternal(newJsonStr);
    }, [selectedTelecommand, jsonData, setEditorValueFromExternal]);

    useEffect(() => {
        if (!editorRef.current && !monacoRef.current) return;
        if (editorRef.current && monacoRef.current) {
            editorRef.current.updateOptions({
                automaticLayout: true,
                minimap: { enabled: false },
                readOnly,
            });

            monacoRef.current.editor.defineTheme("dark-theme", {
                base: "vs-dark",
                inherit: true,
                rules: [],
                colors: {
                    "scrollbarSlider.background": "#808080",
                    "scrollbarSlider.hoverBackground": "#808080",
                    "scrollbarSlider.activeBackground": "#808080",
                },
            });

            monacoRef.current.editor.setTheme("dark-theme");

            monacoRef.current.languages.json.jsonDefaults.setDiagnosticsOptions({
                validate: true,
                allowComments: true,
                schemaValidation: "ignore",
                trailingCommas: "ignore",
            });

            editorRef.current.onDidBlurEditorText(() => {
                isUserEditingRef.current = false;
                setJsonInput(editorRef.current.getValue());
            });
        }
    }, [readOnly]);

    useEffect(() => {
        if (!jsonData) return;
        if (Object.keys(jsonData).length === 0) {
            setJsonInput(EMPTY_JSON_STR);
        }
    }, [jsonData]);

    return (
        <div className="dynamic-form">
            <CommandDataHeader
                isCommandDataJson={isCommandDataJson}
                selectedTelecommand={selectedTelecommand}
                onToggleClick={handleBtnClick}
            />

            {isJsonView && isCommandDataJson ? (
                <JsonEditorView
                    jsonData={jsonData}
                    jsonInput={typeof jsonInput === "string" ? jsonInput : stringifyJson(jsonInput)}
                    jsonError={jsonError}
                    count={jsonEditorKeyRef.current}
                    onJsonChange={handleJsonChange}
                    onEditorMount={(editor, monaco) => {
                        editorRef.current = editor;
                        monacoRef.current = monaco;
                        // Sync editor to correct value on mount — defaultValue may be stale
                        // if the editor first renders after a command is selected in JSON view
                        const expectedValue = selectedTelecommand
                            ? stringifyJson(jsonData)
                            : EMPTY_JSON_STR;
                        if (editor.getValue() !== expectedValue) {
                            editor.setValue(expectedValue);
                        }
                    }}
                />
            ) : (
                <FormFieldsView
                    formContent={formContent}
                    scrollClassName={scrollClassName}
                    mode={mode}
                />
            )}
        </div>
    );
};

export default DualEditorForm;
